/**
 * Подготовка документа к разбору: текст читается, картинки рисуются.
 *
 * В электронном чеке из приложения банка или магазина лежит **настоящий
 * текст**. Прежде мы этого не замечали и рисовали такой файл в JPEG, чтобы
 * отправить распознаванию, — точные числа превращались в пиксели и обратно в
 * числа, с потерями, за деньги и с задержкой. Поэтому сначала снимается
 * текстовый слой, и только когда его нет — страницы рисуются.
 *
 * Почему рисование вообще нужно. Метод распознавания принимает изображение, и
 * передать ему PDF, просто сменив расширение, нельзя: получится бинарный мусор
 * вместо чека. Страницы нужно нарисовать — и делает это браузер, где уже есть
 * холст и шрифты.
 *
 * Здесь же проходит первая проверка честности документа: тип берётся из
 * содержимого файла, а не из имени. Переименованный архив не должен попадать
 * в обработку и тратить чужие деньги.
 */
import { MAX_RECEIPT_PAGES } from '@skoka/core';
import { prepareReceipt, type PreparedImage } from './image.js';

/** Сколько пикселей по длинной стороне достаточно, чтобы прочесть снимок. */
const PAGE_SIDE = 1600;
/**
 * Ширина страницы документа в пикселях.
 *
 * Считать по длинной стороне здесь нельзя: чек — узкая лента высотой в пять
 * своих ширин, и ограничение длинной стороны сжимало её до 249 пикселей по
 * ширине. Буквы в такой странице меньше пикселя, и распознавание читало кашу.
 * Разборчивость задаёт ширина, поэтому меряем по ней.
 */
const PAGE_WIDTH = 1240;
/** Выше этого страницу не растим: у распознавания есть предел на картинку. */
const PAGE_MAX_HEIGHT = 6000;
/** Меньше этого числа строк — текстового слоя фактически нет. */
const TEXT_ENOUGH = 8;
const QUALITY = 0.82;

export interface PreparedDocument {
  /**
   * Страницы как изображения, в порядке документа.
   *
   * Пусто, когда документ прочитан текстом: рисовать страницы незачем, а
   * рисование — самая долгая часть подготовки.
   */
  pages: PreparedImage[];
  /**
   * Напечатанные строки документа сверху вниз.
   *
   * Пусто у снимка и у PDF без текстового слоя — такие идут распознаванию.
   */
  lines: string[];
  /** Те же строки по страницам: выписка разбирается страница за страницей. */
  pageLines: string[][];
  /** Хеш файла: по нему ловится повторная загрузка того же документа. */
  fileHash: string;
  /** Сколько страниц в документе всего — может быть больше, чем взято. */
  totalPages: number;
  kind: 'pdf' | 'image';
}

/**
 * Настоящий тип файла — по сигнатуре, а не по расширению.
 *
 * Браузер ставит `file.type` из имени, и переименованный файл соврёт. Для
 * платного разбора этого достаточно, чтобы отправить в модель что угодно.
 */
export async function sniffType(file: Blob): Promise<string | null> {
  const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const starts = (...bytes: number[]): boolean => bytes.every((b, i) => head[i] === b);

  if (starts(0x25, 0x50, 0x44, 0x46)) return 'application/pdf';          // %PDF
  if (starts(0x89, 0x50, 0x4e, 0x47)) return 'image/png';
  if (starts(0xff, 0xd8, 0xff)) return 'image/jpeg';
  return null;
}

/** Хеш файла для опознания повтора. */
export async function hashFile(file: Blob): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Документ в виде страниц-изображений.
 *
 * Многостраничный PDF обрезается по пределу: чек длиннее десяти страниц — это
 * уже не чек, и разбирать его целиком значит платить за чужую ошибку.
 */
export async function prepareDocument(file: File): Promise<PreparedDocument> {
  const type = (await sniffType(file)) ?? file.type;
  const fileHash = await hashFile(file);

  if (type === 'application/pdf') {
    // Сначала текст: он точен, бесплатен и мгновенен. Рисуем только тогда,
    // когда читать нечего — у сканированного PDF текстового слоя нет.
    const text = await readPdfText(file);
    const lines = text.pages.flat();
    if (lines.length >= TEXT_ENOUGH) {
      return { pages: [], lines, pageLines: text.pages, fileHash, totalPages: text.total, kind: 'pdf' };
    }
    const pages = await renderPdf(file);
    return { pages: pages.images, lines: [], pageLines: [], fileHash, totalPages: pages.total, kind: 'pdf' };
  }
  if (type === 'image/png' || type === 'image/jpeg') {
    return {
      pages: [await prepareReceipt(file, PAGE_SIDE)],
      lines: [], pageLines: [], fileHash, totalPages: 1, kind: 'image',
    };
  }
  throw new Error('Это не PDF, не PNG и не JPEG — проверь, что загружаешь чек');
}

/**
 * Похоже ли на выписку — то же правило, что на сервере (`looksLikeStatement`).
 *
 * У выписки у каждой строки своя дата: три и больше разных дат чек не
 * печатает. Такую выписку незачем рисовать в картинки и распознавать — её
 * текст уже есть в файле, и разбор получает его как есть.
 */
export function looksLikeStatement(lines: readonly string[]): boolean {
  const text = lines.join('\n');
  if (/выписк[аи]\s+(по|из|о)\s|account statement/i.test(text)) return true;
  return new Set(text.match(/\b\d{2}\.\d{2}\.(\d{4}|\d{2})\b/g) ?? []).size >= 3;
}

/**
 * Страницы документа, нарисованные в JPEG.
 *
 * Отдельно от подготовки: к ним обращаются, когда текстовый слой прочитался,
 * но чеком не оказался, — например, у PDF с текстом незнакомой вёрстки.
 */
export async function renderDocument(file: File): Promise<PreparedImage[]> {
  const type = (await sniffType(file)) ?? file.type;
  if (type !== 'application/pdf') return [await prepareReceipt(file, PAGE_SIDE)];
  return (await renderPdf(file)).images;
}

/**
 * Напечатанный текст PDF: строки сверху вниз.
 *
 * Слова в PDF лежат россыпью кусков с координатами, а не строками. Строку
 * собирает общая высота, порядок внутри неё — положение слева направо.
 *
 * Отдельная забота — жирный шрифт: он нарисован двойной печатью со сдвигом в
 * доли пункта, и без этого «Бананы» превращались бы в «Бананы Бананы».
 */
async function readPdfText(file: File): Promise<{ pages: string[][]; total: number }> {
  const pdfjs = await loadPdfjs();
  const data = new Uint8Array(await file.arrayBuffer());
  const doc = await pdfjs.getDocument({ data, isEvalSupported: false }).promise;
  const take = Math.min(doc.numPages, MAX_RECEIPT_PAGES);
  const pages: string[][] = [];

  for (let n = 1; n <= take; n++) {
    const lines: string[] = [];
    const content = await (await doc.getPage(n)).getTextContent();
    const rows: { y: number; parts: { x: number; s: string }[] }[] = [];

    for (const item of content.items) {
      const piece = item as { str?: string; transform?: number[] };
      const str = piece.str ?? '';
      const transform = piece.transform;
      if (!str.trim() || !transform) continue;
      const y = transform[5] ?? 0;
      const x = transform[4] ?? 0;
      // Одна строка бумаги — куски на одной высоте. Допуск в два пункта
      // прощает мелкие сдвиги, но не путает соседние строки: между ними
      // в чеке больше десяти.
      let row = rows.find((r) => Math.abs(r.y - y) <= 2);
      if (!row) { row = { y, parts: [] }; rows.push(row); }
      row.parts.push({ x, s: str });
    }

    rows.sort((a, b) => b.y - a.y);
    for (const row of rows) {
      row.parts.sort((a, b) => a.x - b.x);
      const kept: { x: number; s: string }[] = [];
      for (const part of row.parts) {
        const prev = kept[kept.length - 1];
        if (prev && prev.s === part.s && Math.abs(prev.x - part.x) < 1.5) continue;
        kept.push(part);
      }
      const text = kept.map((p) => p.s).join(' ').replace(/\s+/g, ' ').trim();
      if (text) lines.push(text);
    }
    pages.push(lines);
  }

  const total = doc.numPages;
  await doc.destroy();
  return { pages, total };
}

/**
 * Страницы PDF, нарисованные в JPEG.
 *
 * Библиотека грузится по требованию: она весит больше, чем всё остальное
 * приложение, а нужна тем, кто действительно загружает документы. Человек,
 * записывающий траты руками, платить за неё загрузкой не должен.
 */
async function renderPdf(file: File): Promise<{ images: PreparedImage[]; total: number }> {
  const pdfjs = await loadPdfjs();
  const data = new Uint8Array(await file.arrayBuffer());
  const doc = await pdfjs.getDocument({ data, isEvalSupported: false }).promise;
  const take = Math.min(doc.numPages, MAX_RECEIPT_PAGES);
  const images: PreparedImage[] = [];

  for (let n = 1; n <= take; n++) {
    const page = await doc.getPage(n);
    const base = page.getViewport({ scale: 1 });
    // По ширине, а не по длинной стороне: чек — лента, и длинная сторона
    // ничего не говорит о том, разберут ли буквы. Высоту при этом держим в
    // пределах, которые принимает распознавание.
    const scale = Math.min(PAGE_WIDTH / base.width, PAGE_MAX_HEIGHT / base.height);
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Браузер не дал холст для страницы документа');
    // Белый фон обязателен: у PDF страница прозрачная, а у JPEG прозрачности
    // нет — без заливки текст лёг бы на чёрное и не прочитался.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;

    const dataUrl = canvas.toDataURL('image/jpeg', QUALITY);
    const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
    images.push({
      base64, mimeType: 'image/jpeg',
      width: canvas.width, height: canvas.height,
      bytes: Math.floor((base64.length * 3) / 4),
    });
  }

  await doc.destroy();
  return { images, total: doc.numPages };
}

/**
 * Библиотека PDF: грузится по требованию и один раз на страницу.
 *
 * Она весит больше, чем всё остальное приложение, а нужна тем, кто
 * действительно загружает документы. Человек, записывающий траты руками,
 * платить за неё загрузкой не должен.
 */
async function loadPdfjs(): Promise<typeof import('pdfjs-dist')> {
  const pdfjs = await import('pdfjs-dist');
  // Рабочий поток берётся из той же сборки: иначе версия ядра и версия
  // воркера разъезжаются, и разбор падает на файле, который открывается везде.
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url,
  ).toString();
  return pdfjs;
}
